import { Link } from 'react-router-dom';
import { Sparkles, ArrowUpRight } from 'lucide-react';

/**
 * ProfileEvaluationCallout
 * ----------------------------------------------------------------------------
 * Contextual conversion widget injected into University & Career dynamic
 * layouts. Routes traffic to the centralized Free Profile Evaluation engine
 * while preserving the canonical source attribution tag in the query string.
 *
 * Props:
 *  - context: 'university' | 'career' (drives the headline copy)
 *  - countryHint: optional country slug — forwarded so the wizard can pre-bias
 *    the user's destination selection if we want to later.
 *  - sourceVariant: lets callers tag the click origin (e.g. 'uni-sidebar',
 *    'career-prefooter') for Funnel Velocity attribution.
 */
const ProfileEvaluationCallout = ({
  context = 'university',
  countryHint,
  sourceVariant = 'sidebar',
}) => {
  const headline =
    context === 'career'
      ? 'Not sure if your profile qualifies for this career path?'
      : 'Not sure if your profile qualifies for this university?';

  const subline =
    context === 'career'
      ? 'Our 60-second evaluator maps your academics, language scores, and budget against global career pathways — instantly.'
      : 'Our 60-second evaluator instantly maps your academics, language scores, and budget against world-class universities.';

  const sourceTag = `study-abroad-free-profile-evaluation-${sourceVariant}`;
  const target = `/study-abroad/free-profile-evaluation/?source=${encodeURIComponent(sourceTag)}${
    countryHint ? `&country=${encodeURIComponent(countryHint)}` : ''
  }`;

  return (
    <section
      className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#003087] via-[#001F5C] to-[#020617] border border-[#C5A028]/30 shadow-2xl shadow-black/30"
      data-testid={`profile-eval-callout-${context}`}
    >
      {/* Decorative glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#C5A028]/12 blur-3xl"
      />
      <div className="relative p-6 sm:p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6">
        <div className="flex-1 min-w-0">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C5A028]/15 border border-[#C5A028]/40 text-[10px] font-bold tracking-[0.22em] text-[#FACC15] uppercase mb-4">
            <Sparkles className="w-3 h-3" />
            Free · 60-second engine
          </div>
          <h3 className="text-xl sm:text-2xl md:text-3xl font-bold sa-display text-white leading-tight mb-2">
            {headline}
          </h3>
          <p className="text-sm sm:text-[15px] text-slate-300 leading-relaxed max-w-xl">
            {subline}
          </p>
        </div>

        <div className="shrink-0">
          <Link
            to={target}
            className="inline-flex items-center justify-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm sm:text-base px-6 sm:px-7 py-3.5 rounded-full transition-all hover:shadow-xl hover:shadow-[#C5A028]/40 hover:-translate-y-0.5 whitespace-nowrap"
            data-testid={`profile-eval-callout-cta-${context}`}
          >
            Evaluate Your Profile For Free
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ProfileEvaluationCallout;
